import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { CountdownTimerService } from './countdown-timer.service';
import { EnvironmentService } from './environment.service';

export interface DeliveryEstimate {
  sameDayDispatch: string;
  expressDelivery: string;
  countdown?: string;
}

@Injectable({
  providedIn: 'root',
})
export class DeliveryMessagingService {
  private australianStates = {
    VIC: 'Victoria',
    NSW: 'New South Wales',
    SA: 'South Australia',
    TAS: 'Tasmania',
    QLD: 'Queensland',
    WA: 'Western Australia',
    NT: 'Northern Territory',
    ACT: 'Australian Capital Territory',
  };

  constructor(
    private environmentService: EnvironmentService,
    private countdownTimerService: CountdownTimerService
  ) {}

  /**
   * Check if current time is within business hours (12:01 AM to 12:00 PM AEST on business days)
   */
  private isWithinBusinessHours(): boolean {
    try {
      const now = new Date();

      // Check if it's a business day (Monday = 1, Tuesday = 2, ..., Friday = 5)
      const dayOfWeek = now.getDay();
      if (dayOfWeek === 0 || dayOfWeek === 6) {
        // Sunday = 0, Saturday = 6
        return false;
      }

      // Get current time in AEST
      const userTimezoneOffset = now.getTimezoneOffset() * 60000; // Convert to milliseconds
      const aestOffset = 10 * 60 * 60000; // AEST is UTC+10 (in milliseconds)
      const nowInAEST = now.getTime() + userTimezoneOffset + aestOffset;

      // Create a date object for the current time in AEST
      const nowAEST = new Date(nowInAEST);

      // Get hours and minutes in AEST
      const hours = nowAEST.getHours();
      const minutes = nowAEST.getMinutes();

      // Convert to total minutes since midnight for easier comparison
      const totalMinutes = hours * 60 + minutes;

      // Business hours: 12:01 AM to 12:00 PM AEST
      // 12:01 AM = 1 minute, 12:00 PM = 720 minutes
      return totalMinutes >= 1 && totalMinutes <= 720;
    } catch (error) {
      console.error('Error checking business hours:', error);
      return false;
    }
  }

  /**
   * Get delivery messaging for Australian users based on their state
   */
  getAustralianDeliveryMessage(userState?: string): DeliveryEstimate | null {
    try {
      // Only show for Australian store
      if (this.environmentService.environment.store !== 'AU') {
        return null;
      }

      // Check if we're within business hours
      const isBusinessHours = this.isWithinBusinessHours();

      // Only show same day dispatch message during business hours
      const sameDayDispatch = isBusinessHours
        ? 'Same Day Dispatch for orders placed in the next'
        : 'Next shipping dispatch cut off in';

      let expressDelivery = '';

      if (userState) {
        const stateCode = userState.toUpperCase();
        switch (stateCode) {
          case 'VIC':
          case 'NSW':
          case 'SA':
          case 'TAS':
            expressDelivery =
              'Earliest Express Delivery ' + stateCode + ' - Next business day';
            break;
          case 'QLD':
          case 'WA':
            expressDelivery =
              'Earliest Express Delivery ' + stateCode + ' - 2 business days';
            break;
          case 'NT':
            expressDelivery =
              'Earliest Express Delivery ' + stateCode + ' - 3 business days';
            break;
          default:
            break;
        }
      }

      // Only return the object if we have content to show
      if (!sameDayDispatch && !expressDelivery) {
        return null;
      }

      return {
        sameDayDispatch,
        expressDelivery,
      };
    } catch (error) {
      console.error('Error getting Australian delivery message:', error);
      return null;
    }
  }

  /**
   * Get delivery messaging with countdown timer included in the message
   */
  getAustralianDeliveryMessageWithCountdown(
    userState?: string
  ): Observable<DeliveryEstimate | null> {
    const countdown$ = this.countdownTimerService.getFormattedCountdown();
    const deliveryMessage = this.getAustralianDeliveryMessage(userState);

    if (!deliveryMessage) {
      return new Observable((subscriber) => subscriber.next(null));
    }

    return countdown$.pipe(
      map((countdown) => {
        // Only include countdown if we have a same day dispatch message
        if (deliveryMessage.sameDayDispatch) {
          const countdownText =
            countdown !== 'Deadline passed' ? ` ${countdown} hours` : '';
          const sameDayDispatchWithCountdown =
            deliveryMessage.sameDayDispatch + countdownText;

          return {
            ...deliveryMessage,
            sameDayDispatch: sameDayDispatchWithCountdown,
            countdown,
          };
        }

        // If no same day dispatch message, return the delivery message as is
        return {
          ...deliveryMessage,
          countdown,
        };
      })
    );
  }

  /**
   * Check if user is in Australia based on country code
   */
  isUserInAustralia(countryCode?: string): boolean {
    try {
      return countryCode?.toUpperCase() === 'AU';
    } catch (error) {
      console.error('Error checking if user is in Australia:', error);
      return false;
    }
  }

  /**
   * Get state name from state code
   */
  getStateName(stateCode: string): string {
    try {
      return (
        this.australianStates[
          stateCode.toUpperCase() as keyof typeof this.australianStates
        ] || stateCode
      );
    } catch (error) {
      console.error('Error getting state name:', error);
      return stateCode;
    }
  }
}
